import { MdLock, MdLockOpen, MdSync, MdWarning } from 'react-icons/md';
import {
  formatCompactRelativeTime,
  getConversationActivityTimestamp,
  getConversationDisplayName,
  getLastMessagePreview,
  getMessageSenderName,
  isOwnMessage,
} from '../utils/formatters';
import {
  readMessengerLastOpenedAtMs,
  resolveEffectiveUnreadCount,
} from '../utils/messengerUnreadUtils';
import { getKeySyncStatusLabel } from '../utils/e2eeKeySyncReasons';
import { Avatar } from './Avatar';

const GENERIC_PREVIEWS = new Set(['New message', 'You sent a message', 'No messages yet']);

const resolveConversationKey = (conversation) =>
  conversation?.id ?? conversation?.conversation_id ?? null;

const resolveMessageTimestamp = (message) =>
  message?.sent_at ?? message?.sentAt ?? message?.created_at ?? message?.createdAt ?? null;

const toTimestampMs = (value) => {
  if (!value) return null;
  const timestampMs = new Date(value).getTime();
  return Number.isFinite(timestampMs) ? timestampMs : null;
};

const resolveLatestLoadedMessageTimestamp = (messages = []) => {
  if (!Array.isArray(messages) || !messages.length) return null;

  return messages.reduce((latest, message) => {
    const messageTimestamp = resolveMessageTimestamp(message);
    const messageMs = toTimestampMs(messageTimestamp);
    const latestMs = toTimestampMs(latest);

    if (messageMs == null) return latest;
    if (latestMs == null || messageMs > latestMs) return messageTimestamp;
    return latest;
  }, null);
};

const resolveConversationListTimestamp = (conversation, messagesByConversation = {}) => {
  const conversationKey = resolveConversationKey(conversation);
  const loadedMessages =
    conversationKey != null ? messagesByConversation?.[conversationKey] || [] : [];
  const loadedTimestamp = resolveLatestLoadedMessageTimestamp(loadedMessages);
  const summaryTimestamp = getConversationActivityTimestamp(conversation);
  const loadedTimestampMs = toTimestampMs(loadedTimestamp);
  const summaryTimestampMs = toTimestampMs(summaryTimestamp);

  if (loadedTimestampMs != null && summaryTimestampMs != null) {
    return loadedTimestampMs > summaryTimestampMs ? loadedTimestamp : summaryTimestamp;
  }

  return loadedTimestamp || summaryTimestamp;
};

export function E2EEStatusIcon({
  conversationId,
  keySyncStatusByConversation,
  keySyncReasonByConversation,
  missingKeyConversationIds,
}) {
  const idKey = `${conversationId}`;
  const status = keySyncStatusByConversation?.[idKey] || null;
  const reason = keySyncReasonByConversation?.[idKey] || null;
  const isMissing = (missingKeyConversationIds || []).some((id) => `${id}` === idKey);
  const statusLabel = getKeySyncStatusLabel(status || (isMissing ? 'missing' : null), reason);

  if (status === 'requesting' || status === 'waiting' || status === 'hydrating') {
    return (
      <MdSync
        size={11}
        className="responder-messenger-e2ee-icon responder-messenger-e2ee-icon--syncing"
        title={statusLabel}
        aria-label={statusLabel}
      />
    );
  }
  if (status === 'error') {
    return (
      <MdWarning
        size={11}
        className="responder-messenger-e2ee-icon responder-messenger-e2ee-icon--error"
        title={statusLabel}
        aria-label={statusLabel}
      />
    );
  }
  if (status === 'missing' || isMissing) {
    return (
      <MdLockOpen
        size={11}
        className="responder-messenger-e2ee-icon responder-messenger-e2ee-icon--error"
        title={statusLabel}
        aria-label={statusLabel}
      />
    );
  }
  return (
    <MdLock
      size={11}
      className="responder-messenger-e2ee-icon responder-messenger-e2ee-icon--synced"
      title="Encryption keys synced"
      aria-label="Encryption keys synced"
    />
  );
}

/**
 * Individual conversation item in the list.
 */
function ConversationItem({
  conversation,
  isSelected,
  currentUserId,
  onClick,
  lastOpenedAtMs,
  hasLiveCall,
  messagesByConversation,
  keySyncStatusByConversation,
  keySyncReasonByConversation,
  missingKeyConversationIds,
}) {
  const displayName = getConversationDisplayName(conversation, currentUserId);
  const summaryPreview = getLastMessagePreview(conversation, currentUserId);
  const preview = (() => {
    if (!GENERIC_PREVIEWS.has(summaryPreview)) return summaryPreview;
    const conversationKey = resolveConversationKey(conversation);
    const loadedMessages =
      conversationKey != null ? messagesByConversation?.[conversationKey] : null;
    if (!Array.isArray(loadedMessages) || loadedMessages.length === 0) return summaryPreview;
    const lastMsg = loadedMessages[loadedMessages.length - 1];
    const text = typeof lastMsg?.content === 'string' ? lastMsg.content.trim() : '';
    if (!text) return summaryPreview;
    const participants = conversation?.participants || [];
    const senderName = isOwnMessage(lastMsg, currentUserId)
      ? 'You'
      : getMessageSenderName(lastMsg, participants);
    return `${senderName}: ${text}`;
  })();
  const activityTimestamp = resolveConversationListTimestamp(conversation, messagesByConversation);
  const time = formatCompactRelativeTime(activityTimestamp);
  const unreadCount = resolveEffectiveUnreadCount(conversation, lastOpenedAtMs);

  return (
    <button
      type="button"
      className={`responder-messenger-row${isSelected ? ' is-active' : ''}`}
      onClick={onClick}
    >
      <Avatar label={displayName} group={conversation.type === 'group'} />
      <div className="responder-messenger-row__content">
        <div className="responder-messenger-row__top">
          <span className="responder-messenger-row__title">
            <E2EEStatusIcon
              conversationId={conversation.id}
              keySyncStatusByConversation={keySyncStatusByConversation}
              keySyncReasonByConversation={keySyncReasonByConversation}
              missingKeyConversationIds={missingKeyConversationIds}
            />
            {displayName}
          </span>
          <span className="responder-messenger-row__time">{time}</span>
        </div>
        <div className="responder-messenger-row__bottom">
          <span className="responder-messenger-row__preview">{preview}</span>
          {hasLiveCall && (
            <span
              className="responder-messenger-row__call-dot"
              title="Ongoing call"
              aria-label="Ongoing call"
            />
          )}
          {unreadCount > 0 && <span className="responder-messenger-row__badge">{unreadCount}</span>}
        </div>
      </div>
    </button>
  );
}

/**
 * List of all conversations with search.
 * @param {{ conversations: Array, selectedId: number|null, currentUserId: number, onSelectConversation: (id) => void, loading: boolean, liveCallConversationId: string|number|null }} props
 */
export function ConversationList({
  conversations,
  selectedId,
  currentUserId,
  onSelectConversation,
  loading,
  searchQuery,
  onSearchChange,
  liveCallConversationId,
  messagesByConversation,
  keySyncStatusByConversation,
  keySyncReasonByConversation,
  missingKeyConversationIds,
}) {
  const lastOpenedAtMs = readMessengerLastOpenedAtMs();
  const filteredConversations = conversations
    .filter((conv) => {
      if (!searchQuery) return true;
      const displayName = getConversationDisplayName(conv, currentUserId);
      const preview = getLastMessagePreview(conv, currentUserId);
      return (
        displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        preview.toLowerCase().includes(searchQuery.toLowerCase())
      );
    })
    .map((conversation, index) => ({ conversation, index }))
    .sort((a, b) => {
      const aMs = toTimestampMs(
        resolveConversationListTimestamp(a.conversation, messagesByConversation),
      );
      const bMs = toTimestampMs(
        resolveConversationListTimestamp(b.conversation, messagesByConversation),
      );
      const aValid = Number.isFinite(aMs);
      const bValid = Number.isFinite(bMs);

      if (aValid && bValid && aMs !== bMs) return bMs - aMs;
      if (aValid && !bValid) return -1;
      if (!aValid && bValid) return 1;
      return a.index - b.index;
    })
    .map(({ conversation }) => conversation);

  return (
    <div className="responder-messenger-list">
      {loading ? (
        <div className="responder-messenger-empty">Loading conversations...</div>
      ) : filteredConversations.length === 0 ? (
        <div className="responder-messenger-empty">
          {searchQuery ? 'No conversations match your search.' : 'No conversations yet.'}
        </div>
      ) : (
        filteredConversations.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            conversation={conversation}
            isSelected={selectedId === conversation.id}
            currentUserId={currentUserId}
            lastOpenedAtMs={lastOpenedAtMs}
            onClick={() => onSelectConversation(conversation.id)}
            hasLiveCall={
              liveCallConversationId != null && `${conversation.id}` === `${liveCallConversationId}`
            }
            messagesByConversation={messagesByConversation}
            keySyncStatusByConversation={keySyncStatusByConversation}
            keySyncReasonByConversation={keySyncReasonByConversation}
            missingKeyConversationIds={missingKeyConversationIds}
          />
        ))
      )}
    </div>
  );
}
